import React, { useState, useEffect } from "react";
import NavBar from "./NavBar";
import NavBarMobile from "./NavBarMobile";
import { useMediaQuery } from "@uidotdev/usehooks";
import Form from "./Form";
import Footer from "./Footer";
import imageUvod from "../catalog/fig_iz_rada_objasnjen_hw.png";
import imageMetodologija from "../catalog/hw_methodology_belgrade.png";
import imageTrendoviDays from "../catalog/hw_days_fldmean_timeseries_trend.png";
import imageTrendoviEvents from "../catalog/hw_events_fldmean_timeseries_trend.png";
import imageTrendoviMeanmag from "../catalog/hw_meanmag_fldmean_timeseries_trend.png";
import imageUticaji from "../catalog/toplotni-talasi-uticaj.png";

import {
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
} from "@material-tailwind/react";
import ReactHtmlParser from "html-react-parser";

// Učitavanje svih slika iz lokalnih foldera pomoću require.context
const imagesProslost = require.context(
  "../toplotni-talasi-proslost-slike",
  false,
  /\.(png|jpe?g|svg)$/,
);

const imagesBuducnost = require.context(
  "../toplotni-talasi-buducnost",
  false,
  /\.(png|jpe?g|svg)$/,
);

// NetCDF fajlovi za prošlost
const ncProslost = require.context(
  "../fajlovi-toplotni-talasi-proslost",
  false,
  /\.nc$/,
);

// NetCDF fajlovi za budućnost
const ncBuducnost = require.context(
  "../fajlovi-toplotni-talasi-buducnost",
  false,
  /\.nc$/,
);

export default function ToplotniTalasi() {
  const isSmallDevice = useMediaQuery("only screen and (max-width : 768px)");
  const isTabletOrLarger = useMediaQuery("only screen and (min-width: 769px)");
  const [activeTab, setActiveTab] = useState("uvod");
  const [activePodaciTab, setActivePodaciTab] = useState(
    "toplotni-talasi-u-proslosti",
  );

  const [indeks, setIndeks] = useState("hw_days");
  const [year, setYear] = useState("1950");
  const [yearBuducnost, setYearBuducnost] = useState("2041-2060");

  // -------------------------------------------------------
  // PROŠLOST: hw_days_1950.jpg, hw_events_2003.jpg, itd.
  // -------------------------------------------------------
  const getSelectedImageForHistory = () => {
    const imageName = `./${indeks}_${year}.jpg`;
    try {
      return imagesProslost(imageName);
    } catch (e) {
      console.error("Slika nije pronađena (prošlost):", imageName);
      return null;
    }
  };

  const getDownloadDataLinkForHistory = () => {
    const fileName = `./${indeks}_year_eobs_${year}.nc`;
    try {
      return ncProslost(fileName);
    } catch (e) {
      console.error("NetCDF nije pronađen (prošlost):", fileName);
      return null;
    }
  };

  // -------------------------------------------------------
  // BUDUĆNOST: hw_days_2041-2060_allpct.jpg, itd.
  // -------------------------------------------------------
  const getSelectedImageForProjections = () => {
    const imageName = `./${indeks}_${yearBuducnost}_allpct.jpg`;
    try {
      return imagesBuducnost(imageName);
    } catch (e) {
      console.error("Slika nije pronađena (budućnost):", imageName);
      return null;
    }
  };

  const getDownloadDataLinkForProjections = () => {
    const fileName = `./mod_${indeks}_${yearBuducnost}_hist_rcp45_rcp85_allpct.nc`;
    try {
      return ncBuducnost(fileName);
    } catch (e) {
      console.error("NetCDF nije pronađen (budućnost):", fileName);
      return null;
    }
  };

  const getTrendImage = () => {
    switch (indeks) {
      case "hw_days":
        return imageTrendoviDays;
      case "hw_events":
        return imageTrendoviEvents;
      case "hw_meanmag":
        return imageTrendoviMeanmag;
      default:
        return imageTrendoviDays;
    }
  };

  const data = [
    {
      label: "UVOD",
      value: "uvod",
      desc:
        `<h2>UVOD</h2>
  <p style="margin-bottom: 10px; color: #000;">
    Toplotni talasi se često definišu kao produženi periodi neuobičajeno visokih temperatura u odnosu na očekivane klimatske uslove za određeno vreme i mesto. Iako se najčešće opisuju kao periodi ekstremno visokih temperatura vazduha, njihova pojava i razvoj rezultat su kombinacije dinamičkih i termodinamičkih faktora koji deluju na različitim prostornim i vremenskim razmerama. Na njih utiču brze promene u atmosferskoj cirkulaciji, ali i sporiji procesi u klimatskom sistemu, poput procesa u gornjim slojevima okeana. Dugoročno, ključnu ulogu u porastu učestalosti, trajanja i intenziteta toplotnih talasa imaju antropogeni uticaji, uključujući povećanje koncentracije gasova sa efektom staklene bašte i prenamene zemljišta.
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    Atmosferska cirkulacija predstavlja ključni pokretač toplotnih talasa, pri čemu u umerenim geografskim širinama oni nastaju usled postojanih blokirajućih anticiklona koji omogućavaju zadržavanje toplog vazduha i pojačano zagrevanje iznad nekog regiona. Tri glavna mehanizma koja doprinose razvoju toplotnih talasa su adijabatsko zagrevanje usled subsidencije vazduha, pojačano radijaciono zagrevanje usled smanjene oblačnosti i horizontalna advekcija toplog vazduha. Na regionalnom nivou, faktori poput aerosola, prenamene zemljišta i interakcije tlo-atmosfera (npr. smanjeno isparavanje kada je tlo suvo, te više energije odlazi na osetnu toplotu) mogu dodatno pojačati intenzitet i trajanje toplotnih talasa. U urbanim sredinama, dodatni faktor predstavlja akumulacija toplote u materijalima poput betona i smanjena efikasnost izračivanja tokom noći, što dovodi do viših noćnih temperatura i povećanog toplotnog stresa.
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    Slika je preuzeta iz Barriopedro et al. (2023) i prikazuje neke od pokretača toplotnih talasa i kako oni deluju kroz međusobno povezane procese na različitim prostornim i vremenskim razmerama.
  </p>` +
        `<p style="color: #000;">
    Barriopedro, D., García‐Herrera, R., Ordóñez, C., Miralles, D.G. and Salcedo‐Sanz, S., 2023. Heat waves: Physical understanding and scientific challenges. Reviews of Geophysics, 61(2), p.e2022RG000780.
  </p>`,
      image: `<img src="${imageUvod}" alt="Toplotni Talasi Uvod" style="max-width:100%;height:auto;">`,
      desc2: `<p></p>`,
    },
    {
      label: "METODOLOGIJA",
      value: "metodologija",
      desc:
        `<h2>METODOLOGIJA</h2>
  <p style="margin-bottom: 10px; color: #000;">
    Postoji veliki broj definicija toplotnih talasa. Prema Svetskoj meteorološkoj organizaciji (WMO), toplotni talas se definiše kao „izraženo zagrevanje vazduha ili prodor veoma toplog vazduha iznad većeg područja". Različiti pristupi definisanju toplotnih talasa odražavaju i njihovu višedimenzionalnu prirodu, pri čemu se u literaturi najčešće analiziraju učestalost, trajanje i intenzitet događaja, dok se ređe razmatraju dodatni aspekti, poput vremena pojave ili prostornog obuhvata.
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    Jedan od najčešće korišćenih pristupa zasniva se na percentilskim indeksima, kojima se definišu pragovi ekstremnih vrednosti u odnosu na lokalnu klimatologiju. Ovakav pristup omogućava poređenje između različitih regiona i delova godine, jer uzima u obzir lokalne klimatske karakteristike.
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    Toplotni talasi su identifikovani na osnovu dnevnih maksimalnih temperatura vazduha (<strong>TX</strong>). Kao osnovni prag korišćen je <strong>90. percentil</strong> maksimalne dnevne temperature definisan u odnosu na referentni period 1961–1990. za svaki kalendarski dan u toku godine.
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    Za svaku tačku mreže ispitano je da li TX prelazi prag, a toplotni talas je definisan kao niz od najmanje pet uzastopnih dana tokom kojih maksimalna temperatura prelazi <strong>90. percentil</strong>. Za svaki identifikovani događaj određeni su datum početka, datum završetka i trajanje. Indikator <strong>hw_events</strong> predstavlja ukupan broj toplotnih talasa u toku godine, dok <strong>hw_days</strong> označava ukupan broj dana u uslovima toplotnih talasa. Intenzitet toplotnih talasa kvantifikovan je u odnosu na definisani prag. Za svaki dan koji pripada toplotnom talasu izračunato je prekoračenje temperature u odnosu na prag (TX − TX90p). Na nivou pojedinačnih događaja definisan je intenzitet kao suma dnevnih prekoračenja tokom trajanja događaja i izračunat je indikator <strong>hw_meanmag</strong> - srednji intenzitet događaja u toku godine.
  </p>`,
      image: `<img src="${imageMetodologija}" alt="Toplotni Talasi Metodologija" style="max-width:100%;height:auto;">`,
      desc2: `<p></p>`,
    },
    {
      label: "TRENDOVI",
      value: "trendovi",
      desc:
        `<h2>TRENDOVI</h2>
  <p style="margin-bottom: 10px; color: #000;">
    Na globalnom nivou, toplotni talasi su u poslednjim decenijama postali učestaliji, dugotrajniji i intenzivniji. U regionima širom sveta javljaju se ekstremni toplotni talasi koji bi bez porasta koncentracija gasova sa efektom staklene bašte bili veoma malo verovatni. Od sredine 20. veka uočava se rastući trend u svim navedenim karakteristikama toplotnih talasa tokom toplog dela godine. Najizraženije promene zabeležene su na Bliskom istoku i u pojedinim tropskim regionima, Evropi, Australiji, istočnoj Aziji i zapadnom delu Severne Amerike.
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    Tokom realizacije projekta EXTREMES analizirane su prostorno-vremenske promene toplotnih talasa u Srbiji korišćenjem vremenskih serija indikatora kojima se opisuje broj toplotnih talasa (hw_events), ukupno trajanje toplotnih talasa (hw_days) i srednji intenzitet (hw_meanmag) u toku godine, izračunatih na osnovu E-OBS podataka prostorne rezolucije 0.1° za period 1950–2024. Utvrđeno je da pozitivni trendovi preovlađuju za svaki indeks i njihove vrednosti po dekadi su prikazani na graficima.
  </p>`,
      image: `
    <div style="display: flex; flex-direction: column; gap: 20px;">
      <img src="${imageTrendoviDays}" alt="Trend broja dana toplotnih talasa" style="max-width:100%;height:auto;">
      <img src="${imageTrendoviEvents}" alt="Trend broja toplotnih talasa" style="max-width:100%;height:auto;">
      <img src="${imageTrendoviMeanmag}" alt="Trend prosečne magnitude toplotnih talasa" style="max-width:100%;height:auto;">
    </div>
  `,
      desc2: `<p></p>`,
    },
    {
      label: "UTICAJI",
      value: "uticaji",
      desc:
        `<h2>UTICAJ</h2>
  <p style="margin-bottom: 10px; color: #000;">
    Toplotni talasi spadaju u najsmrtonosnije prirodne nepogode, čiji uticaji značajno prevazilaze okvire meteorologije. Kao produženi periodi neuobičajeno visokih temperatura u odnosu na lokalne klimatske uslove, oni imaju direktne i indirektne posledice na prirodne i društvene sisteme.
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    Najizraženiji uticaji su na ljudsko zdravlje, gde toplotni talasi povećavaju rizik od toplotnog udara, dehidratacije i smrtnosti, posebno kod osetljivih grupa stanovništva. Procene pokazuju da su u periodu 1998–2017. godine bili odgovorni za više od 166.000 smrtnih slučajeva, dok je izloženost stanovništva ekstremnoj toploti značajno porasla u poslednjim decenijama (Pascaline & Rowena, 2018).
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    Pored direktnih zdravstvenih efekata, toplotni talasi utiču i na funkcionisanje društva i infrastrukture. Povećana potreba za hlađenjem dovodi do rasta potrošnje električne energije, što može uzrokovati preopterećenje elektroenergetskog sistema i prekide u snabdevanju. Istovremeno, visoke temperature smanjuju produktivnost rada na otvorenom i mogu doprineti narušavanju društvene i ekonomske stabilnosti.
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    Uticaji su značajni i u sektoru poljoprivrede i ekosistema. Visoke temperature izazivaju toplotni stres kod biljaka i životinja, smanjuju prinose i povećavaju rizik od degradacije zemljišta. U prirodnim ekosistemima, toplotni talasi mogu dovesti do propadanja šuma, poremećaja u funkcionisanju ekosistema i povećanog rizika od požara.
  </p>` +
        `<p style="color: #000;">
    Pascaline, W. and Rowena, H., 2018. Economic losses, poverty and disaster 1998–2017. United Nations Office for Disaster Risk Reduction.
  </p>`,
      image: `<img src="${imageUticaji}" alt="Toplotni Talasi Uticaji" style="max-width:100%;height:auto;">`,
      desc2: `<p></p>`,
    },
    {
      label: "PODACI",
      value: "podaci",
    },
  ];

  return (
    <>
      {isTabletOrLarger && <NavBar />}
      {isSmallDevice && <NavBarMobile />}
      <div className="container mx-auto text-center min-h-screen md:mb-6">
        <Tabs value={activeTab} className="max-w-screen-xl mx-auto">
          <TabsHeader
            className="bg-transparent flex justify-center flex-wrap md:flex-nowrap mx-2 md:mx-0"
            indicatorProps={{
              className: "bg-gray-900/10 shadow-none !text-gray-900",
            }}
            onChange={(value) => setActiveTab(value)}
          >
            {data.map(({ label, value }) => (
              <Tab className="probaTaba px-2 py-1" key={value} value={value}>
                {label}
              </Tab>
            ))}
          </TabsHeader>
          <TabsBody>
            {data.map(({ value, desc, image, desc2 }) => (
              <TabPanel key={value} value={value}>
                {value === "podaci" ? (
                  // ============================================================
                  // TAB PODACI — sa dva podzaba
                  // ============================================================
                  <Tabs
                    value={activePodaciTab}
                    onChange={(val) => setActivePodaciTab(val)}
                  >
                    <TabsHeader className="bg-gray-200">
                      <Tab value="toplotni-talasi-u-proslosti">
                        Toplotni talasi u prošlosti
                      </Tab>
                      <Tab value="projekcije-za-buducnost">
                        Projekcije za budućnost
                      </Tab>
                    </TabsHeader>
                    <TabsBody>
                      {/* ---- PROŠLOST ---- */}
                      <TabPanel value="toplotni-talasi-u-proslosti">
                        <div
                          className={`suseDeo ${
                            isTabletOrLarger ? "flex" : "flex flex-col"
                          }`}
                        >
                          <div className="suseDeoVeci w-full md:w-2/3 p-4">
                            <h3 className="text-lg font-semibold mb-4">
                              Toplotni talasi u prošlosti
                            </h3>
                            <p className="mb-4">
                              Izaberite indeks i godinu da biste videli sliku:
                            </p>

                            <div className="flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-4">
                              {/* Filter za indeks */}
                              <select
                                value={indeks}
                                onChange={(e) => setIndeks(e.target.value)}
                                className="border p-2 w-full md:w-auto"
                              >
                                <option value="hw_days">
                                  Broj dana (hw_days)
                                </option>
                                <option value="hw_events">
                                  Broj talasa (hw_events)
                                </option>
                                <option value="hw_meanmag">
                                  Prosečna magnituda (hw_meanmag)
                                </option>
                              </select>

                              {/* Filter za godinu */}
                              <select
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                                className="border p-2 w-full md:w-auto"
                              >
                                {Array.from(
                                  { length: 2024 - 1950 + 1 },
                                  (_, index) => (
                                    <option key={index} value={1950 + index}>
                                      {1950 + index}
                                    </option>
                                  ),
                                )}
                              </select>
                            </div>

                            {/* Prikaz slike */}
                            <div className="mt-6 w-full md:w-auto">
                              <h4 className="text-lg font-semibold">
                                Prikazana slika za {indeks}, {year}
                              </h4>
                              {getSelectedImageForHistory() ? (
                                <img
                                  src={getSelectedImageForHistory()}
                                  alt={`Slika za ${indeks}, ${year}`}
                                  className="w-full md:w-auto max-w-md mx-auto"
                                />
                              ) : (
                                <p className="text-red-500 mt-4">
                                  Slika nije dostupna za odabrani izbor.
                                </p>
                              )}
                            </div>

                            {/* Dugmad za preuzimanje */}
                            <div className="mt-6 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-4">
                              {getSelectedImageForHistory() && (
                                <a
                                  href={getSelectedImageForHistory()}
                                  download={`${indeks}_${year}.jpg`}
                                  className="bg-blue-500 font-bold text-white px-4 py-2 rounded text-center"
                                >
                                  Preuzmi sliku
                                </a>
                              )}
                              {getDownloadDataLinkForHistory() && (
                                <a
                                  href={getDownloadDataLinkForHistory()}
                                  download={`${indeks}_year_eobs_${year}.nc`}
                                  className="bg-green-500 font-bold text-white px-4 py-2 rounded text-center"
                                >
                                  Preuzmi podatke
                                </a>
                              )}
                            </div>
                          </div>

                          {/* Bočni tekst */}
                          <div className="suseDeoManji w-full md:w-1/3 p-4 bg-white border-l">
                            <h4 className="text-lg font-semibold">
                              TOPLOTNI TALASI U PROŠLOSTI
                            </h4>
                            <p className="mt-4 text-black">
                              Ovde možete preuzeti indekse hw_events, hw_days i
                              hw_meanmag, izračunate za svaku godinu od 1950. do
                              2024. Indeksi su izračunati korišćenjem E-OBS
                              podataka. Rezultate možete preuzeti u grafičkom
                              formatu ili u numeričkom NetCDF formatu.
                            </p>
                            <p className="mt-4 text-black">
                              E-OBS podaci:
                              <br />
                              Cornes, R.C., van der Schrier, G., van den
                              Besselaar, E.J.M., & Jones, P.D. (2018). An
                              Ensemble Version of the E-OBS Temperature and
                              Precipitation Datasets. Journal of Geophysical
                              Research: Atmospheres, 123, 9391-9409.
                            </p>
                          </div>
                        </div>
                      </TabPanel>

                      {/* ---- BUDUĆNOST ---- */}
                      <TabPanel value="projekcije-za-buducnost">
                        <div
                          className={`suseDeo ${
                            isTabletOrLarger ? "flex" : "flex flex-col"
                          }`}
                        >
                          <div className="suseDeoVeci w-full md:w-2/3 p-4">
                            <h3 className="text-lg font-semibold mb-4">
                              Projekcije za budućnost
                            </h3>
                            <p className="mb-4">
                              Izaberite indeks i period da biste videli
                              projekciju:
                            </p>

                            <div className="flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-4">
                              <select
                                value={indeks}
                                onChange={(e) => setIndeks(e.target.value)}
                                className="border p-2 w-full md:w-auto"
                              >
                                <option value="hw_days">
                                  Broj dana (hw_days)
                                </option>
                                <option value="hw_events">
                                  Broj talasa (hw_events)
                                </option>
                                <option value="hw_meanmag">
                                  Prosečna magnituda (hw_meanmag)
                                </option>
                              </select>

                              {/* Period — samo 3 opcije koje postoje u folderu */}
                              <select
                                value={yearBuducnost}
                                onChange={(e) =>
                                  setYearBuducnost(e.target.value)
                                }
                                className="border p-2 w-full md:w-auto"
                              >
                                <option value="2041-2060">2041-2060</option>
                                <option value="2061-2080">2061-2080</option>
                                <option value="2081-2100">2081-2100</option>
                              </select>
                            </div>

                            {/* Prikaz slike */}
                            <div className="mt-6 w-full md:w-auto">
                              <h4 className="text-lg font-semibold">
                                Prikazana projekcija za {indeks},{" "}
                                {yearBuducnost}
                              </h4>
                              {getSelectedImageForProjections() ? (
                                <img
                                  src={getSelectedImageForProjections()}
                                  alt={`Projekcija za ${indeks}, ${yearBuducnost}`}
                                  className="w-full md:w-auto max-w-md mx-auto"
                                />
                              ) : (
                                <p className="text-red-500 mt-4">
                                  Slika nije dostupna za odabrani izbor.
                                </p>
                              )}
                            </div>

                            {/* Dugmad za preuzimanje */}
                            <div className="mt-6 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-4">
                              {getSelectedImageForProjections() && (
                                <a
                                  href={getSelectedImageForProjections()}
                                  download={`${indeks}_${yearBuducnost}_allpct.jpg`}
                                  className="bg-blue-500 font-bold text-white px-4 py-2 rounded text-center"
                                >
                                  Preuzmi projekciju
                                </a>
                              )}
                              {getDownloadDataLinkForProjections() && (
                                <a
                                  href={getDownloadDataLinkForProjections()}
                                  download={`mod_${indeks}_${yearBuducnost}_hist_rcp45_rcp85_allpct.nc`}
                                  className="bg-green-500 font-bold text-white px-4 py-2 rounded text-center"
                                >
                                  Preuzmi podatke
                                </a>
                              )}
                            </div>
                          </div>

                          {/* Bočni tekst */}
                          <div className="suseDeoManji w-full md:w-1/3 p-4 border-l">
                            <h4 className="text-lg font-semibold">
                              PROJEKCIJE ZA BUDUĆNOST
                            </h4>
                            <p className="mt-4 text-black">
                              Ovde možete preuzeti projekcije indeksa hw_events,
                              hw_days i hw_meanmag za tri različita vremenska
                              isečka u budućnosti (2041-2060, 2061-2080,
                              2081-2100). Indeksi su izračunati korišćenjem
                              rezultata osam različitih klimatskih modela koji
                              su preuzeti sa Digitalnog atlasa klime Srbije
                              https://atlas-klime.eko.gov.rs. Odabrane
                              projekcije se odnose na dva emisiona scenarija
                              RCP8.5 i RCP4.5. Rezultate možete preuzeti u
                              grafičkom formatu ili u numeričkom NetCDF formatu.
                            </p>
                            <p className="mt-4 mb-2 text-black">
                              Spisak korišćenih regionalnih klimatskih modela:
                            </p>
                            <ul className="text-black text-md pl-1">
                              <li className="pb-1">- CLMcom-CCLM4-8-17</li>
                              <li className="pb-1">- DMI-HIRHAM5</li>
                              <li className="pb-1">- KNMI-RACMO22E</li>
                              <li className="pb-1">- CLMcom-CCLM4-8-17</li>
                              <li className="pb-1">- KNMI-RACMO22E</li>
                              <li className="pb-1">- CLMcom-CCLM4-8-17</li>
                              <li className="pb-1">- MPI-CSC-REMO2009</li>
                              <li>- MPI-CSC-REMO2009</li>
                            </ul>
                          </div>
                        </div>
                      </TabPanel>
                    </TabsBody>
                  </Tabs>
                ) : (
                  // ============================================================
                  // SVE OSTALE TABOVE (Uvod, Metodologija, Trendovi, Uticaji)
                  // ============================================================
                  <div className="suseDeo flex flex-col md:flex-row">
                    <div className="suseDeoVeci w-full md:w-3/5 p-4">
                      {image && ReactHtmlParser(image)}
                      {desc2 && ReactHtmlParser(desc2)}
                    </div>
                    <div className="suseDeoManji w-full md:w-2/5 p-4 bg-white mt-6 md:mt-0">
                      {desc && ReactHtmlParser(desc)}
                    </div>
                  </div>
                )}
              </TabPanel>
            ))}
          </TabsBody>
        </Tabs>
      </div>
      <Form />
      <Footer />
    </>
  );
}
